import React, { useState, useRef, useEffect, useContext, useMemo, useCallback } from "react";
import axios from "axios";
import config from "../../config";
import QuestContext from "../QuestWrapper";
import Pointer from "../../assets/images/pointer.svg";
import XP from "../../assets/images/xp.svg";
import './spinTheWheel.css';
import Shadow from '../../assets/images/Shadow.png';
import General, { hexToGlassColor } from "../../general";
import Loader from "./Loader";
import Coin from '../../assets/images/coin-icon.svg';

interface SpinTheWheelProps {
  userId?: string;
  token?: string;
  uniqueUserId?: string,
  uniqueEmailId?: string,
  questId?: string;
  criteriaId?: string;
  maxSpins?: number;
  rewards?: OfflineRewardData[];
  wheelColors?: {
    primary: string;
    secondary: string;
  };
  isAccumulateXP?: boolean;
  wheelImage?: string;
  winningIndex?: number;
  onSpinComplete?: () => void;
  successCall?: () => void;
  isImgShow?: boolean;
  styleConfig?: {
    claimButtonStyle?: React.CSSProperties;
    wheelContainerStyle?: React.CSSProperties;
    wheelImageStyle?: React.CSSProperties;
    successModalStyle?: React.CSSProperties;
    segmentContentStyle?: React.CSSProperties;
  };
  claimButtonText?: string;
  wheelTextColor?: {
    primary: string;
    secondary: string;
  };
  winningSegmentColor?: string;
  spinText?: {
    text?: string;
    style?: React.CSSProperties;
  },
  spinCenteredText?: string;
  isSecondaryImage?: boolean;
  onGuestUserClick?: () => void;
  onCompletedCriteriaUserClick?: () => void;
  isClaimTitle?: boolean;
  isLoader?: boolean;
  isPoint?: boolean;
  loaderComponent?: React.ReactNode;
  brandTheme?: any;
  questThemeData?: any;
  wheelWidth?: number;
}

interface OfflineRewardData {
  rewardType?: string,
  metadata?: {
    xp?: number;
    imageURL?: string;
    gemsData?: number;
    name?: string;
    title?: string;
    claimTitle?: string;
    secondaryImageURL?: string;
  }
}

interface RewardData {
  title: string;
  value: string;
  image?: string;
  rewardType?: string;
}

const SpinTheWheelOffline: React.FC<SpinTheWheelProps> = ({
  userId,
  token,
  uniqueUserId,
  uniqueEmailId,
  questId,
  criteriaId,
  maxSpins,
  rewards,
  wheelColors,
  isAccumulateXP = false,
  wheelImage,
  winningIndex,
  onSpinComplete,
  successCall,
  isImgShow,
  styleConfig,
  claimButtonText,
  wheelTextColor,
  winningSegmentColor,
  spinText,
  spinCenteredText,
  isSecondaryImage,
  onGuestUserClick,
  onCompletedCriteriaUserClick,
  isClaimTitle,
  isLoader,
  isPoint,
  loaderComponent,
  brandTheme,
  questThemeData,
  wheelWidth
}) => {
  const { apiKey, apiSecret, entityId, apiType } = useContext(QuestContext.Context);
  const [spinCount, setSpinCount] = useState<number>(0);
  const wheelRef = useRef<HTMLDivElement>(null);
  const [rotationAngle, setRotationAngle] = useState<number>(0);
  const [isSpinning, setIsSpinning] = useState<boolean>(false);
  const [rewardImage, setRewardImage] = useState<string>(isPoint ? Coin : XP);
  const [rewardValue, setRewardValue] = useState<string>("1");
  const [rewardSecondaryImage, setRewardSecondaryImage] = useState<string>('');
  const [winningSegmentIndex, setWinningSegmentIndex] = useState<number | null>(null);

  let BACKEND_URL = apiType === "STAGING" ? config.BACKEND_URL_STAGING : config.BACKEND_URL;
  let GeneralFunctions = new General("");

  const [showCongratulations, setShowCongratulations] = useState(false);
  const [isText, setIsText] = useState(true);
  const [Rewards, setRewards] = useState<RewardData[]>([]);
  const [isCriteriaCompleted, setIsCriteriaCompleted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(true);

  useEffect(() => {
    const validatedRewards = rewards?.slice(0, 10);

    let rewardsDetails = validatedRewards?.map((el: any) => {
      const isRewardXP = el?.rewardType === 'REWARD_XP';
      return {
        title: isRewardXP
          ? isPoint
            ? 'Points'
            : 'XP'
          : el?.metadata?.name || el?.metadata?.title,
        value: isRewardXP ? el?.metadata?.xp : el?.metadata?.gemsData,
        image: isRewardXP
          ? isPoint
            ? Coin
            : XP
          : el?.metadata?.logo || el?.metadata?.imageURL,
        rewardType: el?.rewardType,
      };
    });

    setRewards(rewardsDetails || []);
  }, [rewards, isPoint]);

  const segmentCount = Rewards?.length || 0;
  const segmentAngle = segmentCount > 0 ? 360 / segmentCount : 0;
  const wheelSize = wheelWidth || 240;
  const radius = wheelSize / 2;

  const createSegmentPath = useCallback((index: number) => {
    const startAngle = (index * segmentAngle - 90) * (Math.PI / 180);
    const endAngle = ((index + 1) * segmentAngle - 90) * (Math.PI / 180);

    const x1 = radius + radius * Math.cos(startAngle);
    const y1 = radius + radius * Math.sin(startAngle);
    const x2 = radius + radius * Math.cos(endAngle);
    const y2 = radius + radius * Math.sin(endAngle);

    const largeArc = segmentAngle > 180 ? 1 : 0;

    return `M ${radius} ${radius} L ${x1} ${y1} A ${radius * 0.9} ${radius * 0.9} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  }, [segmentAngle, radius, wheelSize]);

  const getTextPosition = useCallback((index: number) => {
    const angle = (index * segmentAngle + segmentAngle / 2 - 90) * (Math.PI / 180);
    const textRadius = radius * 0.85;
    return {
      x: radius + textRadius * Math.cos(angle),
      y: radius + textRadius * Math.sin(angle),
      rotation: index * segmentAngle + segmentAngle / 2
    };
  }, [segmentAngle, radius, wheelSize]);

  const getImageSize = useMemo(() => {
    const baseSize = wheelSize / 15;
    if (segmentCount <= 4) return baseSize * 1.2;
    if (segmentCount <= 8) return baseSize;
    if (segmentCount <= 12) return baseSize * 0.8;
    if (segmentCount <= 16) return baseSize * 0.6;
    return baseSize * 0.5;
  }, [segmentCount, wheelSize]);

  const getFontSize = useMemo(() => {
    const baseSize = wheelSize / 30;
    if (segmentCount <= 4) return baseSize * 1.2;
    if (segmentCount <= 8) return baseSize;
    if (segmentCount <= 12) return baseSize * 0.8;
    if (segmentCount <= 16) return baseSize * 0.6;
    return baseSize * 0.5;
  }, [segmentCount, wheelSize]);

  const handleSpinClick = async () => {
    if (
      wheelRef.current &&
      !isSpinning &&
      !showCongratulations &&
      Rewards.length > 0 &&
      Rewards[0]?.title !== ''
    ) {
      setIsSpinning(true);
      setIsText(false);

      wheelRef.current.style.transition = "transform 0s";
      wheelRef.current.style.transform = `rotate(0deg)`;
      wheelRef.current.offsetHeight;

      try {
        // Determine winning index
        let actualWinningIndex: number;
        if (winningIndex !== undefined && winningIndex >= 0 && winningIndex < Rewards.length) {
          actualWinningIndex = winningIndex;
        } else {
          actualWinningIndex = Math.floor(Math.random() * Rewards.length);
        }

        const reward = Rewards[actualWinningIndex];
        if (reward !== null) {
          const image = reward?.image || (isPoint ? Coin : XP);
          setRewardImage(image);
          const val = `${reward?.value?.toString()} ${reward?.title}` || "1";
          setRewardValue(val);

          // Handle secondary image
          if (isSecondaryImage && reward?.rewardType === 'REWARD_CUSTOM_REWARD') {
            // Try to find secondary image from original rewards data
            const originalReward = rewards?.find((r, idx) => idx === actualWinningIndex);
            if (originalReward?.metadata?.secondaryImageURL) {
              setRewardSecondaryImage(originalReward.metadata.secondaryImageURL);
            }
          }
        }

        if (actualWinningIndex == null || actualWinningIndex == undefined) {
          console.error("API response issue - winningIndex is null or already claimed reward");
          setIsSpinning(false);
          setRotationAngle(0);
          if (onSpinComplete) {
            onSpinComplete();
          }
          setIsText(true);
          return;
        }

        // Calculate stop angle for smooth animation
        const segmentAngle = 360 / Rewards.length;
        const segmentMiddleOffset = segmentAngle / 2;
        const stopAngle = 360 * 4 + segmentAngle * actualWinningIndex + segmentMiddleOffset;
        setRotationAngle(stopAngle);

        // Apply spinning animation
        wheelRef.current.style.transition = "transform 3s cubic-bezier(0.17, 0.67, 0.12, 0.99)";
        wheelRef.current.style.transform = `rotate(${stopAngle}deg)`;

        setTimeout(() => {
          if (wheelRef.current) {
            wheelRef.current.style.transition = "";
            setSpinCount((prevSpinCount) => prevSpinCount + 1);
            setRotationAngle(0);
            setWinningSegmentIndex(actualWinningIndex);

            setTimeout(() => {
              setShowCongratulations(true);
              setIsSpinning(false);
              if (onSpinComplete) {
                onSpinComplete();
              }
            }, 1000);
          }
        }, 3000);
      } catch (error) {
        console.error("Spin error:", error);
        setIsSpinning(false);
        setRotationAngle(0);
        setIsText(true);
      }
    }
  };

  const wrapText = (text: string, maxCharsPerLine: number): string[] => {
    const words = text.split(' ');
    const lines: string[] = [];
    let line = '';

    for (const word of words) {
      if ((line + (line ? ' ' : '') + word).length <= maxCharsPerLine) {
        line += (line ? ' ' : '') + word;
      } else {
        lines.push(line);
        line = word;

        if (lines.length === 2) {
          break;
        }
      }
    }

    if (lines.length < 3 && line) {
      lines.push(line);
    }

    if (lines.length === 3 && words.length > 0) {
      const lastLine = lines[2];
      if (lastLine.length + 3 <= maxCharsPerLine) {
        lines[2] = lastLine + '...';
      } else {
        lines[2] = lastLine.slice(0, maxCharsPerLine - 3).trimEnd() + '...';
      }
    }

    return lines;
  };

  useEffect(() => {
    return () => {
      setIsSpinning(false);
      setRotationAngle(0);
    };
  }, []);

  return (
    <>
      {isLoaded ? (
        <div className="mainContSpinWheel">
          <div className={'spinMainContainerStyle'} style={{
            background: styleConfig?.wheelContainerStyle?.background || '#969696',
            ...styleConfig?.wheelContainerStyle
          }}>
            <div
              style={{
                filter: showCongratulations ? styleConfig?.successModalStyle?.filter || "blur(8.571428298950195px)" : "none"
              }}
              onClick={handleSpinClick}
            >
              <div className={'spinContainerStyle'} ref={wheelRef} style={{
                cursor: isSpinning ? "no-drop" : "pointer",
                position: 'relative',
                width: wheelSize,
                height: wheelSize,
              }}>
                <img src={Shadow} alt="" className="shadowImg" style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: 2
                }} />

                <svg
                  width={wheelSize}
                  height={wheelSize}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    zIndex: 1
                  }}
                >
                  {Rewards.map((reward, index) => {
                    let segmentColor, textColor;

                    if (segmentCount % 2 === 0) {
                      segmentColor = index % 2 === 0
                        ? wheelColors?.primary || brandTheme?.primaryColor || questThemeData?.accentColor || '#FF6B6B'
                        : wheelColors?.secondary || hexToGlassColor(brandTheme?.primaryColor || questThemeData?.accentColor || '#FF6B6B', 0.3) || '#4ECDC4';

                      textColor = index % 2 === 0
                        ? wheelTextColor?.primary || brandTheme?.titleColor || '#FFFFFF'
                        : wheelTextColor?.secondary || brandTheme?.titleColor || '#FFFFFF';
                    } else {
                      const baseColor = wheelColors?.primary || brandTheme?.primaryColor || questThemeData?.accentColor || '#FF6B6B';
                      const colorPattern = index % 3;

                      if (colorPattern === 0) {
                        segmentColor = baseColor;
                      } else if (colorPattern === 1) {
                        segmentColor = wheelColors?.secondary || hexToGlassColor(baseColor, 0.7) || '#4ECDC4';
                      } else {
                        segmentColor = hexToGlassColor(baseColor, 0.5) || '#96CEB4';
                      }

                      textColor = wheelTextColor?.primary || brandTheme?.titleColor || '#FFFFFF';
                    }

                    const isWinningSegment = winningSegmentIndex !== null && (Rewards.length - 1 - winningSegmentIndex) === index;

                    const lines = wrapText(`${Rewards[Rewards.length - index - 1]?.value || ""} ${Rewards[Rewards.length - index - 1]?.title || ""}`, 10);

                    return (
                      <g key={index}>
                        <path
                          d={createSegmentPath(index)}
                          fill={isWinningSegment ? (winningSegmentColor || "#FFDD7CBF") : segmentColor}
                          stroke="#FFF"
                          strokeWidth="1"
                          style={styleConfig?.segmentContentStyle}
                        />

                        {isImgShow && Rewards[Rewards.length - index - 1]?.image && (
                          <image
                            x={getTextPosition(index).x - getImageSize}
                            y={getTextPosition(index).y - getImageSize / 2}
                            width={getImageSize * 2 + 1}
                            height={getImageSize * 1.2}
                            href={Rewards[Rewards.length - index - 1].image}
                            transform={`rotate(${getTextPosition(index).rotation}, ${getTextPosition(index).x}, ${getTextPosition(index).y})`}
                          />
                        )}

                        <text
                          x={getTextPosition(index).x - getImageSize * 1}
                          y={lines.length > 1 ? getTextPosition(index).y - getImageSize / 2 : getTextPosition(index).y + 2}
                          fill={textColor}
                          fontSize={getFontSize + 1}
                          fontWeight="bold"
                          fontFamily="Figtree"
                          textAnchor="end"
                          style={{ maxWidth: `${getImageSize * 3}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          dominantBaseline="middle"
                          transform={`rotate(${getTextPosition(index).rotation - 90}, ${getTextPosition(index).x}, ${getTextPosition(index).y})`}
                        >
                          {lines.map((line, i) => (
                            <tspan x={getTextPosition(index).x - getImageSize * 1} dy={i === 0 ? '0em' : '1.2em'} key={i}>
                              {line}
                            </tspan>
                          ))}
                        </text>
                      </g>
                    );
                  })}
                </svg>
              </div>

              {/* Center Image/Text Container */}
              <div className={'spinCenteredImgContainerStyle'} style={{
                cursor: isSpinning ? "no-drop" : "pointer",
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10
              }}>
                <div className="spinPointerStyle" style={{ clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)" }} />
                <div className={'spinCenteredImgDivStyle'} style={styleConfig?.wheelImageStyle}>
                  {wheelImage ? (
                    <img src={wheelImage} alt="Mystic" className={'spinCenteredImgStyle'} />
                  ) : (
                    <p className={'spinCenteredTextStyle'}>{spinCenteredText || 'Spin'}</p>
                  )}
                </div>
              </div>
            </div>

            {showCongratulations && (
              <div className={'spinSuccessModal'} style={styleConfig?.successModalStyle}>
                <h5 className={'spinH5Style'}>Congratulations</h5>
                <p className={'spinH2Style'}>You have won:</p>
                <div className="rewardImgContainer">
                  <img
                    src={rewardImage}
                    alt="Reward"
                    style={{ width: "60px", height: "60px", marginBottom: "0px" }}
                  />
                  {isSecondaryImage === true && rewardSecondaryImage && winningSegmentIndex !== null && Rewards[winningSegmentIndex].rewardType === 'REWARD_CUSTOM_REWARD' && (
                    <img
                      src={rewardSecondaryImage}
                      alt="RewardSecondary"
                      style={{ width: "60px", height: "60px", marginBottom: "0px" }}
                    />
                  )}
                </div>
                {rewardValue && (
                  <p style={{ textAlign: "center", marginLeft: "-6px" }}>
                    +{rewardValue}
                  </p>
                )}
                <div
                  style={styleConfig?.claimButtonStyle}
                  className={'spinAmazingButtonStyle'}
                  onClick={() => {
                    setShowCongratulations(false);
                    setWinningSegmentIndex(null);
                    setIsText(true);
                    if (successCall) {
                      successCall();
                    }
                  }}
                >
                  <p>{claimButtonText || 'Claim'}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : isLoader && (loaderComponent || <Loader />)
      }
      {isLoaded && (
        <p className="spinText" style={{ width: `${wheelSize + 12}px`, ...spinText?.style }}>
          {isText ? spinText?.text || 'Tap the wheel to spin!' : ''}
        </p>
      )}
    </>
  );
};

export default SpinTheWheelOffline;